/**
 * CR106: Global Theme Tokens - Design System Configuration
 * 
 * This module defines the global design tokens consumed by deal card rendering.
 * These tokens are GLOBAL (apply to all deal types) and separate from:
 * - Per-type templates (Deal Cards tab → sections, ordering, labels)
 * - Per-type registry (ribbon text/theme per deal category)
 * 
 * Theme Labs in Command Center is the visual editor for these tokens.
 */

// ================================================================
// TYPOGRAPHY TOKENS
// ================================================================

export interface TypographyTokens {
  /** Font family for headings/titles */
  headingFont: "serif" | "sans" | "mono";
  /** Font family for body text */
  bodyFont: "serif" | "sans" | "mono";
  /** Base font size scale (affects all text) */
  fontScale: "compact" | "normal" | "large";
  /** Line height preset */
  lineHeight: "tight" | "normal" | "relaxed";
  /** CR109: Heading font ID from fontRegistry (takes precedence over headingFont) */
  headingFontId?: string;
  /** CR109: Body font ID from fontRegistry (takes precedence over bodyFont) */
  bodyFontId?: string;
}

export const DEFAULT_TYPOGRAPHY_TOKENS: TypographyTokens = {
  headingFont: "sans",
  bodyFont: "sans",
  fontScale: "normal",
  lineHeight: "normal",
  headingFontId: "inter",
  bodyFontId: "inter",
};

// ================================================================
// COLOR PALETTE TOKENS
// ================================================================

export interface ColorPaletteTokens {
  /** Card background (HSL value, e.g. "0 0% 100%") */
  cardBackground: string;
  /** Card border color */
  cardBorder: string;
  /** Header background */
  headerBackground: string;
  /** Header text color */
  headerText: string;
  /** Body text color */
  bodyText: string;
  /** Muted/secondary text color */
  mutedText: string;
  /** Accent color for highlights */
  accentColor: string;
}

export const DEFAULT_COLOR_PALETTE: ColorPaletteTokens = {
  cardBackground: "0 0% 100%",
  cardBorder: "0 0% 0% / 0.05",
  headerBackground: "0 0% 10%",
  headerText: "0 0% 100%",
  bodyText: "0 0% 10%",
  mutedText: "0 0% 45%",
  accentColor: "220 60% 50%",
};

// ================================================================
// SPACING TOKENS
// ================================================================

export interface SpacingTokens {
  /** Card padding preset */
  cardPadding: "compact" | "normal" | "spacious";
  /** Section gap preset */
  sectionGap: "tight" | "normal" | "relaxed";
  /** Header height */
  headerHeight: "sm" | "md" | "lg";
}

export const DEFAULT_SPACING_TOKENS: SpacingTokens = {
  cardPadding: "normal",
  sectionGap: "normal",
  headerHeight: "md",
};

// ================================================================
// COMPONENT STYLE TOKENS
// ================================================================

export interface RibbonStyleTokens {
  /** Corner radius for ribbon shape */
  cornerRadius: number;
  /** Padding inside ribbon */
  padding: "compact" | "normal" | "spacious";
  /** Shadow intensity */
  shadow: "none" | "subtle" | "medium" | "strong";
  /** Default ribbon position */
  defaultPosition: "top-right" | "top-left";
}

export interface BadgeStyleTokens {
  /** Badge height preset */
  height: "sm" | "md" | "lg";
  /** Badge border radius */
  borderRadius: "none" | "sm" | "md" | "full";
  /** Border width */
  borderWidth: 0 | 1 | 2;
}

export interface ButtonStyleTokens {
  /** Button border radius */
  borderRadius: "none" | "sm" | "md" | "lg" | "full";
  /** Button size */
  size: "sm" | "md" | "lg";
}

export interface StarburstStyleTokens {
  /** Starburst size */
  size: "sm" | "md" | "lg";
  /** Default position */
  defaultPosition: "top-left" | "top-right";
  /** Offset X (px) */
  offsetX: number;
  /** Offset Y (px) */
  offsetY: number;
  /** 
   * CR142: Asset Studio asset ID for the starburst shape.
   * If set, uses the SVG from Asset Studio instead of hardcoded shape.
   * If null/undefined, falls back to default asset or built-in fallback.
   */
  assetId: string | null;
}

// ================================================================
// MERCHANT NAME PLACEMENT TOKENS (CR-MerchantName)
// ================================================================

/** Where the merchant name appears in the card */
export type MerchantNamePlacement = 
  | "HIDDEN"           // Do not render
  | "HEADER_TITLE"     // In the header band (current default)
  | "BODY_TOP"         // Top of card body, above chips
  | "BODY_BELOW_CHIPS" // Under the chips row
  | "BODY_ABOVE_DETAILS" // Before detail pills/content
  | "BODY_FOOTER";     // Near bottom, above "Why this is a deal"

/** Text size/style for the merchant name */
export type MerchantNameStyle = "TITLE" | "SUBTITLE" | "SMALL";

export interface MerchantNameTokens {
  /** Where the merchant name appears */
  placement: MerchantNamePlacement;
  /** Text style/size */
  style: MerchantNameStyle;
}

export const DEFAULT_MERCHANT_NAME_TOKENS: MerchantNameTokens = {
  placement: "HEADER_TITLE", // Current behavior
  style: "TITLE",
};

export interface ComponentStyleTokens {
  ribbon: RibbonStyleTokens;
  badge: BadgeStyleTokens;
  button: ButtonStyleTokens;
  starburst: StarburstStyleTokens;
  /** CR-MerchantName: Merchant name display settings */
  merchantName: MerchantNameTokens;
  /** Card corner radius (px) */
  cardCornerRadius: number;
  /** Card shadow intensity */
  cardShadow: "none" | "low" | "medium" | "high";
  /** Card border width (px) */
  cardBorderWidth: number;
}

export const DEFAULT_COMPONENT_TOKENS: ComponentStyleTokens = {
  ribbon: {
    cornerRadius: 0,
    padding: "normal",
    shadow: "subtle",
    defaultPosition: "top-right",
  },
  badge: {
    height: "sm",
    borderRadius: "full",
    borderWidth: 1,
  },
  button: {
    borderRadius: "full",
    size: "md",
  },
  starburst: {
    size: "md",
    defaultPosition: "top-left",
    offsetX: -28,
    offsetY: -20,
    assetId: null, // CR142: Uses default from Asset Studio or built-in fallback
  },
  merchantName: DEFAULT_MERCHANT_NAME_TOKENS,
  cardCornerRadius: 24,
  cardShadow: "medium",
  cardBorderWidth: 1,
};

// ================================================================
// THEME PRESET TOKENS
// ================================================================

export type ThemePresetKey = "light" | "dark" | "custom";

export interface ThemePresetTokens {
  /** Active preset */
  activePreset: ThemePresetKey;
  /** Custom overrides when preset is "custom" */
  customOverrides: Partial<GlobalThemeTokens>;
}

// ================================================================
// DEAL TYPE THEME COLORS (CR339)
// ================================================================

export interface DealTypeThemeColors {
  /** Header/accent background for this deal type (HEX) */
  headerBackgroundHex: string;
  /** Ribbon base fill for this deal type (HEX) */
  ribbonBaseHex: string;
}

/** Default per-deal-type theme colors matching legacy registry theme mapping */
export const DEFAULT_DEAL_TYPE_THEMES: Record<string, DealTypeThemeColors> = {
  BONUS: { headerBackgroundHex: "#059669", ribbonBaseHex: "#059669" },
  DISCOUNT: { headerBackgroundHex: "#F97316", ribbonBaseHex: "#F97316" },
  POINTS: { headerBackgroundHex: "#9333EA", ribbonBaseHex: "#9333EA" },
  IN_KIND: { headerBackgroundHex: "#D97706", ribbonBaseHex: "#D97706" },
  UNCLASSIFIED: { headerBackgroundHex: "#475569", ribbonBaseHex: "#475569" },
};

// ================================================================
// FULL GLOBAL THEME CONFIG
// ================================================================

export interface GlobalThemeTokens {
  typography: TypographyTokens;
  colors: ColorPaletteTokens;
  spacing: SpacingTokens;
  components: ComponentStyleTokens;
  /** CR339: Per-deal-type theme colors (centralized in Theme Labs) */
  dealTypeThemes?: Record<string, DealTypeThemeColors>;
}

export const DEFAULT_GLOBAL_THEME: GlobalThemeTokens = {
  typography: DEFAULT_TYPOGRAPHY_TOKENS,
  colors: DEFAULT_COLOR_PALETTE,
  spacing: DEFAULT_SPACING_TOKENS,
  components: DEFAULT_COMPONENT_TOKENS,
  dealTypeThemes: DEFAULT_DEAL_TYPE_THEMES,
};

// ================================================================
// PRESET DEFINITIONS
// ================================================================

export const THEME_PRESETS: Record<ThemePresetKey, Partial<GlobalThemeTokens>> = {
  light: {
    colors: {
      cardBackground: "0 0% 100%",
      cardBorder: "0 0% 0% / 0.05",
      headerBackground: "0 0% 10%",
      headerText: "0 0% 100%",
      bodyText: "0 0% 10%",
      mutedText: "0 0% 45%",
      accentColor: "220 60% 50%",
    },
    components: {
      ...DEFAULT_COMPONENT_TOKENS,
      cardShadow: "medium",
    },
  },
  dark: {
    colors: {
      cardBackground: "220 30% 14%",
      cardBorder: "220 20% 25% / 0.5",
      headerBackground: "220 35% 8%",
      headerText: "45 20% 92%",
      bodyText: "45 20% 92%",
      mutedText: "45 15% 60%",
      accentColor: "38 65% 55%",
    },
    components: {
      ...DEFAULT_COMPONENT_TOKENS,
      cardShadow: "low",
      cardCornerRadius: 28,
    },
  },
  custom: {},
};

// ================================================================
// CSS HELPERS
// ================================================================

export const FONT_FAMILY_MAP: Record<TypographyTokens["headingFont"], string> = {
  serif: "'Crimson Pro', Georgia, serif",
  sans: "system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

export const FONT_SCALE_MAP: Record<TypographyTokens["fontScale"], number> = {
  compact: 0.875,
  normal: 1,
  large: 1.125,
};

export const LINE_HEIGHT_MAP: Record<TypographyTokens["lineHeight"], number> = {
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.75,
};

export const CARD_PADDING_MAP: Record<SpacingTokens["cardPadding"], string> = {
  compact: "1rem",
  normal: "1.5rem",
  spacious: "2rem",
};

export const SECTION_GAP_MAP: Record<SpacingTokens["sectionGap"], string> = {
  tight: "0.5rem",
  normal: "1rem",
  relaxed: "1.5rem",
};

export const HEADER_HEIGHT_MAP: Record<SpacingTokens["headerHeight"], string> = {
  sm: "4rem",
  md: "5rem",
  lg: "6rem",
};

export const CARD_SHADOW_MAP: Record<ComponentStyleTokens["cardShadow"], string> = {
  none: "none",
  low: "0 4px 12px rgba(0,0,0,0.04)",
  medium: "0 10px 30px rgba(0,0,0,0.08)",
  high: "0 20px 50px rgba(0,0,0,0.15)",
};

export const BADGE_HEIGHT_MAP: Record<BadgeStyleTokens["height"], string> = {
  sm: "1.5rem",
  md: "1.75rem",
  lg: "2rem",
};

export const BADGE_RADIUS_MAP: Record<BadgeStyleTokens["borderRadius"], string> = {
  none: "0",
  sm: "0.25rem",
  md: "0.5rem",
  full: "9999px",
};

export const BUTTON_RADIUS_MAP: Record<ButtonStyleTokens["borderRadius"], string> = {
  none: "0",
  sm: "0.25rem",
  md: "0.5rem",
  lg: "0.75rem",
  full: "9999px",
};

export const STARBURST_SIZE_MAP: Record<StarburstStyleTokens["size"], { width: string; height: string }> = {
  sm: { width: "4rem", height: "4rem" },
  md: { width: "6rem", height: "6rem" },
  lg: { width: "7rem", height: "7rem" },
};

/**
 * Convert HSL string to CSS hsl() value
 */
export function hslToCss(hsl: string): string {
  if (!hsl) return "transparent";
  const hasAlpha = hsl.includes("/");
  if (hasAlpha) {
    const [hslPart, alpha] = hsl.split("/").map((s) => s.trim());
    const [h, s, l] = hslPart.split(" ").map((s) => s.trim());
    return `hsla(${h}, ${s}, ${l}, ${alpha})`;
  }
  const [h, s, l] = hsl.split(" ").map((s) => s.trim());
  return `hsl(${h}, ${s}, ${l})`;
}
